import React, { useState, useEffect } from 'react';
import { Box, Text, Table, TableBody, TableCell, TableRow, Button, Spinner as SpinnerGrommet } from "grommet"
import { SpinnerDots } from './Spinner';

import envConfig from '../../utils/envConfig'
import { CircleAlert, StatusGood } from 'grommet-icons';
import TTLogo from '../../assets/ttlogo.png'
import { ChosenBackend } from './backend'
import axios from 'axios'
import { detect } from 'detect-browser';

export const Spinner = () => (
    <SpinnerGrommet
        border={[
            { side: 'all', color: 'transparent', size: 'medium' },
            { side: 'horizontal', color: 'brand', size: 'medium' },
        ]}
    />
);

const isExt = /ext-|prod/.test(envConfig[process.env.REACT_APP_ENVIRONMENT]["titlebar_name"])
const liteMode = envConfig[process.env.REACT_APP_ENVIRONMENT]['lite']
const isLocal = process.env.REACT_APP_ENVIRONMENT.includes('local')

export const readAccessScope = "triage/r"

export const ShowInit = ({ titlebarCheck, puserCheck, personId, scopes, gqlCheck }) => {
    return (
        <Box fill background="light-2" justify="center" align="center">
            <Box
                background="light-1"
                align="center"
                justify="start"
                alignContent="start"
                pad="medium"
                gap="medium"
                elevation="large"
                maxWidth="300px"
                border={{
                    side: "all",
                    size: "5px",
                    color: "brand",
                    style: "double"
                }}
            >
                <Box>
                    <img src={TTLogo} alt="Trading Technologies" height="70px" width="250px" />
                </Box>
                <Box justify="center" align="center" alignContent='center' gap="small">
                    <Text weight="bold" color="dark-3" size="large">
                        Initializing TT Control Center UI <SpinnerDots />
                    </Text>
                </Box>
                <Box direction='row' axWidth="300px">
                    <InitInfo personId={personId} />
                    <InitChecks
                        titlebarCheck={titlebarCheck}
                        scopes={scopes}
                        puserCheck={puserCheck}
                        gqlCheck={gqlCheck}
                    />
                </Box>
            </Box>
        </Box>
    )
}

const InitChecks = ({ titlebarCheck, puserCheck, scopes, gqlCheck }) => {
    const jbCheck = useCheckOnJB()
    const thisBrowser = detect()
    const { isOnline } = useNetworkStatus();
    const lnaCheck = useLnaCheck({ isChrome: thisBrowser.name.includes("chrome") })

    return (
        <Box pad="small" justify="center" align="center" gap="small">
            <Text weight="bold" color="brand" size="medium">
                Session Status
            </Text>
            <Table>
                <TableBody>
                    <TableRow>
                        <TableCell scope="row">
                            <strong>Internet Browser</strong>
                        </TableCell>
                        <TableCell>
                            <Box direction="row" gap="small" align="center">
                                {thisBrowser.name.includes("chrome") && <StatusGood size="24px" color="ttok" />}
                                {!thisBrowser.name.includes("chrome") && <CircleAlert size="24px" color="ttdown" />}
                                {!thisBrowser.name.includes("chrome") &&
                                    <Text size="12px" color="ttdown">
                                        Only Chrome Browser is Officially Supported
                                    </Text>
                                }
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell scope="row">
                            <strong>Internet Connection</strong>
                        </TableCell>
                        <TableCell>
                            {isOnline && <StatusGood size="24px" color="ttok" />}
                            {!isOnline && <CircleAlert size="24px" color="ttdown" />}
                        </TableCell>
                    </TableRow>
                    {(isExt && !liteMode && !isLocal) &&
                        <TableRow>
                            <TableCell scope="row">
                                <strong>Jumpbox Connection</strong>
                            </TableCell>
                            <TableCell width="180px">
                                <Text size="12px" color="ttdown" wordBreak="break-word">
                                    {jbCheck !== "init" && jbCheck && <StatusGood size="24px" color="ttok" />}
                                    {jbCheck === "init" && <Spinner />}
                                    {jbCheck !== "init" && !jbCheck && <CircleAlert size="24px" color="ttdown" />}
                                    {jbCheck !== "init" && !jbCheck && "    Required When Using External Control Center"}
                                </Text>
                            </TableCell>
                        </TableRow>
                    }
                    <TableRow>
                        <TableCell scope="row">
                            <strong>Titlebar Login</strong>
                        </TableCell>
                        <TableCell>
                            <Box direction="row" gap={"small"} align="center">
                                {titlebarCheck && <StatusGood size="24px" color="ttok" />}
                                {!titlebarCheck && <CircleAlert size="24px" color="ttdown" />}
                                {!titlebarCheck &&
                                    <>
                                        <Text size="12px" color="ttdown">
                                            You must enable 3rd party cookies on your browser
                                        </Text>
                                        <Button
                                            margin={{ left: "5px" }}
                                            size="small"
                                            href="https://support.google.com/accounts/answer/61416?hl=en&co=GENIE.Platform%3DDesktop"
                                            target='_blank'
                                            primary
                                            label={<Text size="12px">Instructions</Text>}
                                        />
                                    </>
                                }
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell scope="row">
                            <strong>TTID Scope</strong>
                        </TableCell>
                        {titlebarCheck &&
                            <TableCell>
                                <Box direction="row" gap={"small"} align="center">
                                    {scopes && scopes.includes(readAccessScope) && <StatusGood size="24px" color="ttok" />}
                                    {!scopes && <Spinner />}
                                    {scopes && !scopes.includes(readAccessScope) && <CircleAlert size="24px" color="ttdown" />}
                                    {scopes && !scopes.includes(readAccessScope) &&
                                        <Text size="12px" color="ttdown">
                                            Scope "{readAccessScope}" is required. Please request it in #ttus-help.
                                        </Text>
                                    }
                                </Box>
                            </TableCell>
                        }
                    </TableRow>
                    {thisBrowser.name.includes("chrome") &&
                        <TableRow>
                            <TableCell scope="row">
                                <strong>Chrome Local Network Access</strong>
                            </TableCell>
                            <TableCell>
                                <Box direction="row" gap={"small"} align="center">
                                    {lnaCheck === "granted" && <StatusGood size="24px" color="ttok" />}
                                    {lnaCheck !== "granted" && <CircleAlert size="24px" color="ttdown" />}
                                    {lnaCheck !== "granted" &&
                                        <>
                                            <Text size="12px" color="ttdown">
                                                Please allow local network access on chrome.
                                            </Text>
                                            <Button
                                                margin={{ left: "5px" }}
                                                size="small"
                                                href="https://tradingtech.atlassian.net/wiki/x/CYBxIg"
                                                target='_blank'
                                                primary
                                                label={<Text size="12px">More information</Text>}
                                            />
                                        </>
                                    }
                                </Box>
                            </TableCell>
                        </TableRow>
                    }
                    <TableRow>
                        <TableCell scope="row">
                            <strong>Graphql Endpoint Connection</strong>
                        </TableCell>
                        <TableCell>
                            <Box direction="row" gap={"small"} align="center">
                                {gqlCheck && gqlCheck !== "loading" && <StatusGood size="24px" color="ttok" />}
                                {gqlCheck && gqlCheck === "loading" && <Spinner />}
                                {!gqlCheck && <CircleAlert size="24px" color="ttdown" />}
                                {!gqlCheck &&
                                    <>
                                        <Text size="12px" color="ttdown">
                                            Re-authenticate your Cloudflare session?
                                        </Text>
                                        <Button
                                            margin={{ left: "5px" }}
                                            size="small"
                                            href="https://tradingtechnologies.cloudflareaccess.com/cdn-cgi/access/refresh-identity"
                                            target='_blank'
                                            primary
                                            label={<Text size="12px">Cloudflare</Text>}
                                        />
                                    </>
                                }
                            </Box>
                        </TableCell>
                    </TableRow>
                    {/* Person Id Check should only happen after gql check is successful, to reduce confusion */}
                    {gqlCheck && gqlCheck !== "loading" &&
                        <TableRow>
                            <TableCell scope="row">
                                <strong>PersonId Data Load</strong>
                            </TableCell>
                            <TableCell>
                                <Box direction="row" gap={"small"} align="center">
                                    {puserCheck === "loading" && <Spinner />}
                                    {puserCheck && puserCheck !== "loading" && <StatusGood size="24px" color="ttok" />}
                                    {!puserCheck && puserCheck !== "loading" && <CircleAlert size="24px" color="ttdown" />}
                                    {!puserCheck && puserCheck !== "loading" &&
                                        <Text size="12px" color="ttdown">
                                            puserId not found.
                                            If this is a new TT ID please wait 1 hour before using Control Center.
                                        </Text>
                                    }
                                </Box>
                            </TableCell>
                        </TableRow>
                    }
                </TableBody>
            </Table>
        </Box>

    )
}

export const InitInfo = ({ personId }) => {
    const thisBrowser = detect()
    return (
        <Box pad="small" justify="center" align="center" gap="small">
            <Text weight="bold" color="brand" size="medium">
                Session Details
            </Text>
            <Table height="100%">
                <TableBody>
                    {personId &&
                        <TableRow>
                            <TableCell scope="row">
                                <strong>User PuserID</strong>
                            </TableCell>
                            <TableCell>{personId}</TableCell>
                        </TableRow>
                    }
                    <TableRow>
                        <TableCell scope="row">
                            <strong>OS</strong>
                        </TableCell>
                        <TableCell>{thisBrowser.os}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell scope="row">
                            <strong>Browser</strong>
                        </TableCell>
                        <TableCell>{thisBrowser.name} ({thisBrowser.version})</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell scope="row">
                            <strong>Connected to</strong>
                        </TableCell>
                        <TableCell>https://{ChosenBackend().replace("/v1/graphql", "")}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell scope="row">
                            <strong>Control Center Version</strong>
                        </TableCell>
                        <TableCell>{process.env.REACT_APP_VERSION}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    )
}

function useLnaCheck({ isChrome }) {
    // https://docs.google.com/document/d/1QQkqehw8umtAgz5z0um7THx-aoU251p705FbIQjDuGs/edit?tab=t.0
    const [result, setResult] = useState("")
    useEffect(() => {
        if (isChrome) {
            try {
                navigator.permissions.query({ name: "local-network-access" })
                    .then((result) => {
                        setResult(result.state)
                    })
                    .catch((error) => {
                        // Continue silently if permission query fails
                        console.warn("Local network access permission query failed:", error)
                    })
            } catch (error) {
                // Continue silently if navigator.permissions is not supported
                console.warn("Local network access check not supported:", error)
            }
        }
    }, [isChrome])
    return result
}

function useCheckOnJB() {

    const [urlResponse, setUrlResponse] = useState("init")

    useEffect(() => {
        const url = "https://triage-lab-prod.debesys.net/health"
        const fetchAPI = async () => {
            axios.get(
                url
            ).then(response => {
                setUrlResponse(true)
            }
            ).catch(error => {
                setUrlResponse(false)
            }
            )
        }
        if (isExt && !liteMode && !isLocal) {
            fetchAPI()
        }

        const interval = setInterval(() => {
            if (isExt) {
                fetchAPI()
            }
        }, 20000)

        return () => {
            setUrlResponse(false)
            clearInterval(interval)
        }
    }, [])

    return urlResponse;
}

// https://medium.com/@abdulahad2024/real-time-network-status-detection-in-react-js-next-js-67595c4bd81c
const useNetworkStatus = () => {
    const [isOnline, setOnline] = useState("init");

    const updateNetworkStatus = () => {
        setOnline(navigator.onLine);
    };

    //   sometimes, the load event does not trigger on some browsers, that is why manually calling updateNetworkStatus on initial mount
    useEffect(() => {
        updateNetworkStatus();
    }, []);

    useEffect(() => {
        window.addEventListener("load", updateNetworkStatus);
        window.addEventListener("online", updateNetworkStatus);
        window.addEventListener("offline", updateNetworkStatus);

        return () => {
            window.removeEventListener("load", updateNetworkStatus);
            window.removeEventListener("online", updateNetworkStatus);
            window.removeEventListener("offline", updateNetworkStatus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigator.onLine]);

    return { isOnline };
};